import { useEffect, useMemo, useState } from "react";
import { kStringConstants } from "@utils/constants/StringConstants";
import Alert, { AlertState } from "@components/common/Alert";
import { NLMailboxProfileType, NLProfile, NLSenderProspect, NLUserMailState } from "@models/Models";
import * as analyticsManager from "@utils/managers/AnalyticsManager";
import ParticlesBackground from "@components/common/ParticlesBackground";
import NewsletterPickerRow from "./NewsletterPickerRow";
import * as Date from "@utils/Date+GetDateFor";
import { ReactComponent as LoadingSpinner } from "@images/loading_spinner.svg";
import { useOnboardingProfile } from "@providers/OnboardingContext";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { kAnalyticsConstants } from "@utils/constants/AnalyticsConstants";
import { MailboxIntegrationManager } from "@utils/managers/MailboxIntegrationManager";
import LoadingView, { LoadingState } from "@components/common/LoadingView";
import { useAuth } from "@providers/AuthContext";
import FlashlightLoader from "@components/common/FlashlightLoader";
import { GetSenderProspectsService } from "@utils/managers/backendMailManager/mailboxFunctions/GetSenderProspectsService";
import PopUpModal from "@components/common/PopUpModal";
import verifiedBadge from "@images/verified_badge.png";
import PreviewMessageModal from "./PreviewMessageModal";

const NewsletterPicker = (props) => {
	const { onboardingProfile, setOnboardingProfile } = useOnboardingProfile();
	const location = useLocation();
	const navigate = useNavigate();
	const isAccount: boolean = location.state.isAccount ?? false;
	const { authUser, setAuthUser } = useAuth();
	const [prospects, setProspects] = useState<NLSenderProspect[]>(
		onboardingProfile!.persistence.prospectArray
	);

	const [isLoadingInitial, setIsLoadingInitial] = useState<boolean>(!onboardingProfile!.persistence.prospectLoaded);
	const [lookbackArray, setLookbackArray] = useState<number[]>([-7, -14, -21, -30]);
	const [alertState, setAlertState] = useState<AlertState>({
		isShow: null,
		title: null,
		message: null,
		actionButton: null,
		dismissButton: null,
		actionButtonAction: undefined,
	});
	const [isShowMoreLoading, setShowMoreLoading] = useState<boolean>(false);
	const [isSelectAll, setIsSelectAll] = useState<boolean>(true);
	const [loadingState, setLoadingState] = useState<LoadingState>({
		isLoading: false,
	});
	const [isShowPopUpModal, setIsShowPopUpModal] = useState<boolean>(false);
	const [previewMessageId, setPreviewMessageId] = useState<string | undefined>(undefined);
	const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);

	useEffect(() => {
		var tempOnboardingProfile = { ...onboardingProfile! };
		tempOnboardingProfile.persistence.prospectArray = prospects;
		setOnboardingProfile(tempOnboardingProfile);
	}, [prospects]);

	// Fetch fresh states on mount if we don't have any yet, show flashlight until done
	useEffect(() => {
		
		analyticsManager.recordEvent(kAnalyticsConstants.Onboarding.feedPickerPresented, {
			is_onboarding: !isAccount,
		});

		if (onboardingProfile!.persistence.prospectLoaded) {
			return;
		}

		const mailboxProfile = onboardingProfile!.persistence.mailboxProfile;
		if (!mailboxProfile) {
			setIsLoadingInitial(false);
			return;
		}

		const getSenderProspectsService = new GetSenderProspectsService(mailboxProfile, null, false, null);
		getSenderProspectsService.getSenderProspects((prospects, error) => {
			if (!prospects || error) {
				setAlertState({ isShow: true, title: kStringConstants.Common.errorAlertTitle, message: kStringConstants.Common.errorAlertMessage, actionButton: isAccount ? "OK" : "Continue", dismissButton: null, actionButtonAction: undefined });
				return;
			}

			if (prospects.length == 0) {
				setAlertState({ isShow: true, title: "We couldn't find any newsletters", message: "We couldn't find any newsletters in your inbox from the last 7 days. Please proceed.", actionButton: "OK", dismissButton: null, actionButtonAction: () => navigate("/get-started/organized") });
				return;
			}

			var tempOnboardingProfile = { ...onboardingProfile! };
			const newProspects = prospects.sort((a, b) => (a?.sender_name || "").localeCompare(b?.sender_name || ""))
			 	.sort((a, b) => {
			 		const whitelistedA = Number(a?.is_newsletter) || 0;
			 		const whitelistedB = Number(b?.is_newsletter) || 0;
			 		return whitelistedB - whitelistedA;
			 	});
			tempOnboardingProfile.persistence.prospectArray = newProspects;
			let newsletterProspects = newProspects.filter((x) => x.is_newsletter);
			for (const prospect of newsletterProspects) {
				if (!tempOnboardingProfile.persistence.feedArray.includes(prospect.sender_id)) {
					tempOnboardingProfile.persistence.feedArray.push(prospect.sender_id);
				}
			}
			tempOnboardingProfile.persistence.prospectLoaded = true;
			tempOnboardingProfile.persistence.lastGetUserMailStatesDateString = Date.backendDateString();
			setOnboardingProfile(tempOnboardingProfile);

			setProspects(newProspects);
			setIsLoadingInitial(false);
			setIsShowPopUpModal(true);
		});

	}, []);

	const listItemClicked = (senderId: number) => {
		var tempOnboardingProfile = { ...onboardingProfile! };
		var currentFeed = tempOnboardingProfile.persistence.feedArray;

		if (currentFeed.includes(senderId)) {
			currentFeed = currentFeed.filter((x) => x !== senderId);
		} else {
			currentFeed.push(senderId);
		}

		tempOnboardingProfile.persistence.feedArray = currentFeed;
		setOnboardingProfile(tempOnboardingProfile);
		setIsSelectAll(tempOnboardingProfile.persistence.feedArray.length != tempOnboardingProfile.persistence.prospectArray.length);
	};

	const loadMoreOnClick = (passedLookbackArray) => {
		if (passedLookbackArray.length < 2) {
			setLookbackArray([]);
			return;
		}

		setShowMoreLoading(true);

		let lookbackBeforeItem = passedLookbackArray[0];
		let lookbackAfterItem = passedLookbackArray[1];

		const mailboxProfile = onboardingProfile!.persistence.mailboxProfile;

		const getSenderProspectsService = new GetSenderProspectsService(mailboxProfile!, [lookbackAfterItem, lookbackBeforeItem], false, null);
		getSenderProspectsService.getSenderProspects((prospects, error) => {
			if (!prospects || error) {
				setAlertState({ isShow: true, title: kStringConstants.Common.errorAlertTitle, message: kStringConstants.Common.errorAlertMessage, actionButton: "OK", dismissButton: null, actionButtonAction: undefined });
				return;
			}
			var tempLookbackArray = [...passedLookbackArray];
			tempLookbackArray = tempLookbackArray.slice(1);

			appendNewUserMailStates(prospects, tempLookbackArray);
		});
	};

	const appendNewUserMailStates = (newProspects: NLSenderProspect[], tempLookbackArray) => {
		let originalMailStates = [...prospects];
		var originalOnboardingStates = [...onboardingProfile!.persistence.prospectArray];
		let originalOnboardingStatesSenderAddressArray = originalMailStates.map((state) => state.sender_address);
		let newUserMailStates = newProspects.filter((userMailState) => !originalOnboardingStatesSenderAddressArray.includes(userMailState.sender_address));

		let tempOnboardingProfile = onboardingProfile!;
		originalOnboardingStates.push(...newUserMailStates);
		tempOnboardingProfile.persistence.prospectArray = originalOnboardingStates;
		tempOnboardingProfile.persistence.lastGetUserMailStatesDateString = Date.backendDateString();

		let existingSenderAddresses = originalMailStates.map((state) => state.sender_address);

		let sortedNewUserMailStates = newProspects
			.filter((userMailState) => !existingSenderAddresses.includes(userMailState.sender_address))
			.sort((a, b) => (a?.sender_name || "").localeCompare(b?.sender_name || ""))
			.sort((a, b) => {
				const whitelistedA = Number(a?.is_newsletter) || 0;
				const whitelistedB = Number(b?.is_newsletter) || 0;
				return whitelistedB - whitelistedA;
			});

		originalMailStates.push(...sortedNewUserMailStates);

		setProspects(originalMailStates);
		setOnboardingProfile(tempOnboardingProfile);

		if (sortedNewUserMailStates.length == 0) {
			loadMoreOnClick(tempLookbackArray);
			return;
		}

		setLookbackArray(tempLookbackArray);
		setShowMoreLoading(false);
	};

	const selectAllOnClick = () => {
		var tempOnboardingProfile = { ...onboardingProfile! };
		tempOnboardingProfile.persistence.feedArray = [];

		if (isSelectAll) {
			tempOnboardingProfile.persistence.feedArray = tempOnboardingProfile.persistence.prospectArray.map((x) => x.sender_id);
		}

		setIsSelectAll(!isSelectAll);
		setOnboardingProfile(tempOnboardingProfile);
	};

	const selectAllSuggestedOnClick = () => {
		var tempOnboardingProfile = { ...onboardingProfile! };
		tempOnboardingProfile.persistence.feedArray = [];
		tempOnboardingProfile.persistence.feedArray = tempOnboardingProfile.persistence.prospectArray.filter((x) => x.is_newsletter === true).map((x) => x.sender_id);
		setOnboardingProfile(tempOnboardingProfile);
	};

	const movedToNextPage = () => {
		const ratio: number = (prospects?.filter((state) => state.is_newsletter ?? false).length || 0) / (prospects?.length || 1);
		const doubleRatio: number = Math.round(100 * ratio) / 100;
		analyticsManager.recordEvent(kAnalyticsConstants.Onboarding.feedPickerDone, {
			[kAnalyticsConstants.Onboarding.OnboardingProfileKeys.displayedFeed]: prospects?.length || 0,
			[kAnalyticsConstants.Onboarding.OnboardingProfileKeys.displayedWhitelistedFeed]: prospects?.filter((state) => state.is_newsletter ?? false).length || 0,
			[kAnalyticsConstants.Onboarding.OnboardingProfileKeys.displayedWhitelistedRatio]: doubleRatio,
			[kAnalyticsConstants.Onboarding.OnboardingProfileKeys.progressedLookBackDays]: (lookbackArray[0] || 0) * -1,
			[kAnalyticsConstants.Onboarding.OnboardingProfileKeys.newslettersAdded]: onboardingProfile!.persistence.feedArray.length || 0,
			is_onboarding: !isAccount,
		});
	};

	const applyChanges = () => {
		setLoadingState({ ...loadingState, message: "Applying inbox changes", isLoading: true });
		const mailboxIntegrationManager = new MailboxIntegrationManager(onboardingProfile!, authUser!, MailboxIntegrationManager.Reference.nonSignUp);
		mailboxIntegrationManager
			.applyNewsletterChanges(false)
			.then(([_userMailStatesArray, profile]) => {
				if (profile) {
					const userProfile: NLProfile = profile;
					setAuthUser(userProfile);
				}
				analyticsManager.recordEvent(kAnalyticsConstants.App.appliedNewsletterChanges, { newsletter_changes_count: onboardingProfile!.persistence.feedArray.length, newsletter_changes_error: false, is_onboarding: false });
				navigate("/settings", { replace: true, state: { mailboxIntegrated: onboardingProfile?.persistence.mailboxProfile!.type }});
			})
			.catch((error) => {
				analyticsManager.recordEvent(kAnalyticsConstants.App.appliedNewsletterChanges, { newsletter_changes_count: onboardingProfile!.persistence.feedArray.length, newsletter_changes_error: true, is_onboarding: false, error: error.message ?? "" });
				setAlertState({
					isShow: true,
					title: "Oops something went wrong",
					message: "Some of your selected newsletter changes were not applied, please try again within the Settings.",
					actionButton: "OK",
					dismissButton: null,
					actionButtonAction: () => {
						setLoadingState({ ...loadingState, isLoading: false });
					},
				});
			});
	};

	const handlePreview = (messageId: string) => {
		setPreviewMessageId(messageId);
		setIsPreviewOpen(true);
	};

	return (
		<div>
			<LoadingView loadingState={loadingState} />
			<Alert alertState={alertState} setAlertState={setAlertState} />
			<PopUpModal isShow={isShowPopUpModal} setIsShow={setIsShowPopUpModal} image={verifiedBadge} title="Your newsletters are here!" description="We've automatically pre-selected known newsletters to save you some time. You can easily uncheck any you don't want, and if we missed a favorite, you'll be able to add them later." actionTitle="Got it 🎈" />
            <PreviewMessageModal open={isPreviewOpen} onClose={() => setIsPreviewOpen(false)} messageId={previewMessageId} />
			<ParticlesBackground />
			<div className={`flex min-h-[100dvh] flex-col justify-center py-12`}>
				<div className="fixed top-0 bg-black/50 w-full p-3 z-30 backdrop-blur-lg border-b border-white/10 shadow-2xl">
					<div className="max-w-[1080px] w-full z-30 m-auto">
						<div className="flex justify-end items-center">
							{isAccount ? (
								<button className={`text-white font-medium font-primary text-lg focus:outline-none rounded-xl p-2 px-3 py-2 items-center bg-success-green/80 hover:bg-success-green/100 transition ease-in-out whitespace-nowrap ${isLoadingInitial ? "opacity-50 cursor-not-allowed" : "opacity-100 cursor-pointer"}`} onClick={() => applyChanges()}>Done</button>
							) : (
								<Link to="/get-started/organized" onClick={() => movedToNextPage()}>
									<button disabled={isLoadingInitial} className={`text-white font-medium font-primary text-lg focus:outline-none rounded-xl p-2 px-3 py-2 items-center bg-success-green/80 hover:bg-success-green/100 transition ease-in-out whitespace-nowrap ${isLoadingInitial ? "opacity-50 cursor-not-allowed" : "opacity-100 cursor-pointer"}`}>Next</button>
								</Link>
							)}
						</div>
					</div>
				</div>
				{isLoadingInitial ? (
					<FlashlightLoader />
				) : (
				<div className="flex flex-col gap-10 items-center justify-center p-3 mt-[50px] md:mt-[80px] animate-subtleFadeIn">
					<div className="meco_container max-w-[1080px] py-[40px] md:p-[60px] md:pb-[40px]">
						<div className="flex flex-col md:flex-row gap-5 items-start">
							<div className="sticky md:top-[90px] w-full md:w-1/2 flex flex-col gap-2">
								<div className="text-white font-bold font-primary text-4xl leading-10">Add newsletters to Meco</div>
								<div className="text-force-primary-light font-medium font-primary text-base  md:text-xl">Selected newsletters will appear in Meco instead of your {onboardingProfile?.persistence.mailboxProfile!.type === NLMailboxProfileType.Gmail ? "Gmail" : "Outlook"} inbox going forward. You can go back to viewing these in your inbox at any time.</div>
							</div>
							<div className="w-full md:w-1/2 flex flex-col items-center justify-center gap-3 max-w-[450px] md:max-w-none m-auto md:min-w-[400px]">
								<div className="flex flex-row justify-end w-full gap-2">
									<button className="text-white font-regular font-primary text-sm focus:outline-none rounded-xl p-2 px-3 py-2 items-center border border-white/10 hover:bg-white/20 transition ease-in-out whitespace-nowrap" onClick={() => selectAllSuggestedOnClick()}>
										Select all suggested
									</button>
									<button className="text-white font-regular font-primary text-sm focus:outline-none rounded-xl p-2 px-3 py-2 items-center border border-white/10 hover:bg-white/20 transition ease-in-out whitespace-nowrap" onClick={() => selectAllOnClick()}>
										{isSelectAll ? "Select all" : "Deselect all"}
									</button>
								</div>
								<div className="text-white font-medium font-primary text-3xl text-center w-full max-w-[600px] mb-5  no-select">
								{prospects.map((prospect, i) => {
                                    return <NewsletterPickerRow prospect={prospect} key={i} isChecked={onboardingProfile!.persistence.feedArray.includes(prospect.sender_id)} checkOnChange={(senderId) => listItemClicked(senderId)} onPreview={(messageId) => handlePreview(messageId)} />;
                                })}
									{lookbackArray.length > 0 && (
										<button disabled={isShowMoreLoading} className="flex mt-4 w-full border justify-center h-[50px] items-center rounded-[10px] border-force-primary-light/20 text-base  font-primary font-bold" onClick={() => loadMoreOnClick(lookbackArray)}>
											{isShowMoreLoading ? <LoadingSpinner className="h-[20px] text-white/20 animate-spin fill-white" /> : <div>Show more from last {lookbackArray[0] * -1} days</div>}
										</button>
									)}
								</div>
							</div>
						</div>
					</div>
				</div>
				)}
				</div>
		</div>
	);
};

export default NewsletterPicker;
